import { useState } from "react";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { Loading, Badge, ConfirmDialog } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function ItemRow({ q, cfg, onSaved, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(q.name);
  const tid = cfg.testid;
  const save = async () => {
    try {
      const r = await api.put(cfg.endpoint, { old_name: q.name, new_name: name });
      toast.success(`✓ ${cfg.noun} rinominata`);
      setEditing(false);
      onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (editing) {
    return (
      <li className="flex items-center gap-2 py-2.5" data-testid={`${tid}-row-${q.name}`}>
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="max-w-xs" autoFocus data-testid={`${tid}-rename-input`} />
        <Button size="icon" variant="ghost" onClick={save} data-testid={`${tid}-rename-save`}><Check className="h-4 w-4 text-emerald-400" /></Button>
        <Button size="icon" variant="ghost" onClick={() => { setEditing(false); setName(q.name); }}><X className="h-4 w-4" /></Button>
      </li>
    );
  }
  return (
    <li className="flex items-center gap-2 py-2.5" data-testid={`${tid}-row-${q.name}`}>
      <span className="flex-1 font-medium">{q.name}</span>
      <Badge className="bg-slate-800 border-slate-600 text-slate-300" testid={`${tid}-count-${q.name}`}>{q.count} {cfg.owners}</Badge>
      <Button size="icon" variant="ghost" onClick={() => setEditing(true)} data-testid={`${tid}-edit-${q.name}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => onDelete(q)} data-testid={`${tid}-delete-${q.name}`}><Trash2 className="h-4 w-4" /></Button>
    </li>
  );
}

export const QUALIFICATIONS_CFG = {
  endpoint: "/qualifications", testid: "qualification", noun: "Mansione", owners: "operai", placeholder: "Nuova mansione (es. Saldatore)",
  description: "Le mansioni compaiono nella scheda operaio e nei filtri. Rinominando una mansione si aggiornano anche gli operai che la hanno.",
};
export const SITE_TYPES_CFG = {
  endpoint: "/site-types", testid: "site-type", noun: "Tipologia", owners: "cantieri", placeholder: "Nuova tipologia (es. Restauro)",
  description: "Le tipologie compaiono nella scheda cantiere e nei filtri di ricerca. Rinominando una tipologia si aggiornano anche i cantieri che la hanno.",
};
export const COST_CATEGORIES_CFG = {
  endpoint: "/cost-categories", testid: "cost-category", noun: "Categoria", owners: "acquisti", placeholder: "Nuova categoria (es. Carburante)",
  keepRecords: true,
  description: "Le categorie compaiono negli acquisti e nelle spese di Economia. Rinominando o eliminando una categoria, gli acquisti già registrati restano con il nome che avevano.",
};

const deleteDescription = (cfg, del, noun) => {
  if (!del?.count) return `La ${noun} verrà rimossa dall'elenco.`;
  if (cfg.keepRecords) return `La ${noun} verrà rimossa dall'elenco; i ${del.count} ${cfg.owners} già registrati la mantengono.`;
  return `${del.count} ${cfg.owners} hanno questa ${noun}: cambia prima la loro ${noun}.`;
};

export default function ManagedListPanel({ cfg }) {
  const { data, setData, loading } = useApi(cfg.endpoint);
  const [name, setName] = useState("");
  const [del, setDel] = useState(null);
  if (loading && !data) return <Loading />;
  const add = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(cfg.endpoint, { name });
      setData(r.data);
      setName("");
      toast.success(`✓ ${cfg.noun} aggiunta`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async () => {
    try {
      const r = await api.delete(cfg.endpoint, { params: { name: del.name } });
      setData(r.data);
      toast.success(`✓ ${cfg.noun} eliminata`);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setDel(null);
  };
  const noun = cfg.noun.toLowerCase();
  return (
    <div className="surface p-5 max-w-2xl" data-testid={`${cfg.testid}s-panel`}>
      <p className="text-sm text-muted-foreground mb-4">{cfg.description}</p>
      <form onSubmit={add} className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={cfg.placeholder} data-testid={`${cfg.testid}-new-input`} />
        <Button type="submit" disabled={!name.trim()} data-testid={`${cfg.testid}-add-btn`}><Plus className="h-4 w-4 mr-1" />Aggiungi</Button>
      </form>
      <ul className="divide-y divide-border/60" data-testid={`${cfg.testid}s-list`}>
        {data.items.map((q) => <ItemRow key={q.name} q={q} cfg={cfg} onSaved={setData} onDelete={setDel} />)}
      </ul>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title={`Eliminare la ${noun} "${del?.name}"?`}
        description={deleteDescription(cfg, del, noun)} onConfirm={remove} />
    </div>
  );
}
